import React from 'react';
import * as R from 'ramda';
// components
import { TextComponent } from '../../../components/text';
// features
import { tollChargeOptions } from '../../fleet-profile/settings/integration';
// helpers/constants
import * as G from '../../../helpers';
import * as GC from '../../../constants';
// utilities
import routesMap from '../../../utilities/routes';
// ui
import { Box } from '../../../ui';
//////////////////////////////////////////////////

const tableSettings = {
  minHeight: 320,
  cellFontSize: 13,
  titleFontSize: 14,
  tableRowHeight: 40,
  titleRowHeight: 45,
  allowEditBtn: true,
  allowSelectAll: true,
  checkBoxCellWidth: 30,
  searchableTitles: true,
  allowSelectItems: true,
};

const TableCell = ({ text }) => {
  if (G.isNilOrEmpty(text)) return null;

  return (
    <TextComponent
      title={text}
      display='block'
      withEllipsis={true}
    >
      {text}
    </TextComponent>
  );
};

const getPayrollNumberCustomComponent = (payrollNumberProp) => (props) => {
  const { data, closeModal, setInitialExpandedContainer } = props;

  const payrollNumber = G.getPropFromObject(payrollNumberProp, data);

  if (G.isNilOrEmpty(payrollNumber)) return null;

  return (
    <Box
      cursor='pointer'
      textDecoration='underline'
      color={G.getTheme('colors.dark.blue')}
      onClick={() => {
        if (G.notEquals(G.getCurrentPathname(), GC.ROUTE_PATH_PAYROLLS)) {
          return G.goToRoute(routesMap.driverPayrollList);
        }

        G.callFunction(setInitialExpandedContainer);
        G.callFunction(closeModal);
      }}
    >
      <TableCell text={payrollNumber} />
    </Box>
  );
};

const columnSettings = {
  [GC.FIELD_TOLL_CHARGES_SOURCE]: {
    width: 150,
    name: 'titles:source',
    customComponent: ({ data }) => {
      const source = R.propOr('', GC.FIELD_TOLL_CHARGES_SOURCE, data);

      const displayedValue = R.compose(
        R.propOr(source, GC.FIELD_LABEL),
        R.find(R.propEq(source, GC.FIELD_VALUE)),
      )(tollChargeOptions);

      return <TableCell text={displayedValue} />;
    },
    filter: { type: 'selectMultiple', options: tollChargeOptions },
  },
  [GC.FIELD_TOLL_CHARGES_AMOUNT]: {
    width: 150,
    pivotType: 'number',
    name: 'titles:amount',
    filter: { type: 'number' },
  },
  [GC.FIELD_TOLL_CHARGES_VENDOR_AMOUNT]: {
    width: 150,
    pivotType: 'number',
    filter: { type: 'number' },
    name: 'titles:vendor-amount',
  },
  [GC.FIELD_TOLL_CHARGES_COMPANY_AMOUNT]: {
    width: 150,
    pivotType: 'number',
    filter: { type: 'number' },
    name: 'titles:company-amount',
  },
  [GC.FIELD_CURRENCY]: {
    width: 150,
    name: 'titles:currency',
    options: GC.CURRENCY_OPTIONS_2,
    filter: { type: 'selectMultiple', options: GC.CURRENCY_OPTIONS_2 },
  },
  [GC.FIELD_TOLL_CHARGES_AGENCY]: {
    width: 150,
    name: 'titles:agency',
  },
  [GC.FIELD_TOLL_CHARGES_POSITING_DATE]: {
    width: 220,
    filter: { type: 'date' },
    name: 'titles:posting-date',
    pivotType: GC.PIVOT_TYPE_YQMD,
  },
  [GC.GRC.DRIVER_PAYROLL_NUMBER]: {
    width: 200,
    name: 'titles:payroll-number',
    customComponent: getPayrollNumberCustomComponent(GC.GRC.DRIVER_PAYROLL_NUMBER),
  },
  [GC.GRC.VENDOR_PAYROLL_NUMBER]: {
    width: 200,
    name: 'titles:vendor-payroll-number',
    customComponent: getPayrollNumberCustomComponent(GC.GRC.VENDOR_PAYROLL_NUMBER),
  },
  [GC.FIELD_TOLL_CHARGES_LICENCE_PLATE]: {
    width: 150,
    name: 'titles:license-plate',
  },
  [GC.FIELD_TOLL_CHARGES_LOCATION_NAME]: {
    width: 200,
    name: 'titles:location-name',
  },
  [GC.FIELD_TOLL_CHARGES_TRANSPONDER_ID]: {
    width: 150,
    name: 'titles:transponder-id',
  },
  [GC.FIELD_TOLL_CHARGES_TRANSACTION_DATE]: {
    width: 220,
    pivotType: GC.PIVOT_TYPE_YQMD,
    filter: { type: 'date' },
    name: 'titles:transaction-date',
  },
  [GC.FIELD_TOLL_CHARGES_TRANSACTION_TYPE]: {
    width: 200,
    name: 'titles:transaction-type',
  },
  [GC.GRC.DRIVER_FIRST_NAME]: {
    width: 200,
    name: ['titles:driver', 'titles:first-name'],
    customComponent: ({ data }) => {
      const text = R.or(
        R.pathOr('', [GC.SYSTEM_OBJECT_DRIVER, GC.FIELD_FIRST_NAME], data),
        R.propOr('', GC.GRC.DRIVER_FIRST_NAME, data),
      );

      return <TableCell text={text} />;
    },
  },
  [GC.GRC.DRIVER_LAST_NAME]: {
    width: 200,
    name: ['titles:driver', 'titles:last-name'],
    customComponent: ({ data }) => {
      const text = R.or(
        R.pathOr('', [GC.SYSTEM_OBJECT_DRIVER, GC.FIELD_LAST_NAME], data),
        R.propOr('', GC.GRC.DRIVER_LAST_NAME, data),
      );

      return <TableCell text={text} />;
    },
  },
  [GC.GRC.TRUCK_UNIT_ID]: {
    width: 200,
    name: ['titles:truck', 'titles:unit-id'],
    customComponent: ({ data }) => {
      const text = R.or(
        R.pathOr('', [GC.FIELD_TRUCK, GC.FIELD_TRUCK_UNIT_ID], data),
        R.propOr('', GC.GRC.TRUCK_UNIT_ID, data),
      );

      return <TableCell text={text} />;
    },
  },
  [GC.FIELD_CREATED_DATE]: {
    type: 'date',
    name: 'titles:created-date',
    pivotType: GC.PIVOT_TYPE_YQMD,
  },
  [GC.FIELD_CREATED_BY]: {
    width: 200,
    name: 'titles:created-by',
  },
  [GC.FIELD_LAST_MODIFIED_DATE]: {
    type: 'date',
    name: 'titles:updated-date',
    pivotType: GC.PIVOT_TYPE_YQMD,
  },
  [GC.FIELD_LAST_MODIFIED_BY]: {
    width: 200,
    name: 'titles:updated-by',
  },
  [GC.GRC.BRANCH_DOT_BRANCH_NAME]: {
    width: 200,
    name: 'titles:branch',
  },
};

export {
  tableSettings,
  columnSettings,
};
